/* Reads each section's own background colour and hands it to its neighbours, so
   the gradient in palpa-section-blend.css always resolves to whatever colours
   are currently set. Nothing here hard-codes a palette: change a colour scheme
   in the theme editor and the seams re-blend on the next render.

   Nothing below measures geometry. Sections that build themselves after load —
   the press slider is one — have no height when this first runs, and an earlier
   version that waited for them to grow ended up forcing layout while Swiper was
   mid-initialisation, which threw. Colour and flow are known from the markup
   alone, so reading only computed style keeps this off the layout path. */
(() => {
  const isOpaque = (color) => Boolean(color) && color !== 'transparent' && !/,\s*0\)$/.test(color);

  /* The header is deliberately left out — it carries navigation, not the page's
     field of colour, and a gradient behind it would read as a mistake. The
     footer is included, since it is the last band the page fades into. */
  const candidates = () => {
    const main = document.getElementById('MainContent');
    if (!main) return [];
    const list = [...main.querySelectorAll(':scope > .shopify-section')];
    for (let node = main.nextElementSibling; node; node = node.nextElementSibling) {
      list.push(...(node.matches('.shopify-section') ? [node] : node.querySelectorAll(':scope > .shopify-section')));
    }
    return list;
  };

  /* A section wrapper is transparent; the colour is painted by something inside
     it — `.section` for most, `<footer>` for the footer. Lifted-out elements
     (hidden drawers, the fixed mobile bar) are not part of the page's vertical
     run of colour, so they are passed over and their section reads as a gap. */
  /* Three outcomes, not two. A section that renders but paints nothing is a real
     gap and must break the seam either side of it. A section that renders nothing
     at all is not a band: the promo strip is hidden above 768px, and counting its
     absence as a gap left a hard edge between the two sections it sits between.
     Still no geometry read — `display` alone separates the two cases. */
  const ABSENT = Symbol('absent');

  const backdrop = (wrapper) => {
    const standing = (el) => {
      const style = getComputedStyle(el);
      const lifted = style.display === 'none' || style.position === 'fixed' || style.position === 'absolute';
      return lifted ? null : style;
    };

    const own = standing(wrapper);
    if (own && isOpaque(own.backgroundColor)) return { el: wrapper, color: own.backgroundColor };

    let renders = false;
    for (const el of wrapper.children) {
      const style = standing(el);
      if (!style) continue;
      renders = true;
      if (isOpaque(style.backgroundColor)) return { el, color: style.backgroundColor };
    }
    return renders ? null : ABSENT;
  };

  const blend = () => {
    // Sections that paint nothing stay in the list as nulls, so a transparent
    // band like the video hero correctly breaks the seam either side of it.
    const stack = candidates().map(backdrop).filter((section) => section !== ABSENT);

    stack.forEach((section, i) => {
      if (!section) return;
      const { el, color } = section;
      const neighbour = (other) => (other ? other.color : color);

      el.classList.add('palpa-blend');
      el.style.setProperty('--palpa-blend-self', color);
      el.style.setProperty('--palpa-blend-prev', neighbour(stack[i - 1]));
      el.style.setProperty('--palpa-blend-next', neighbour(stack[i + 1]));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', blend, { once: true });
  } else {
    blend();
  }

  // The theme editor swaps sections in and out without a reload.
  document.addEventListener('shopify:section:load', blend);
  document.addEventListener('shopify:section:unload', blend);
  document.addEventListener('shopify:section:reorder', blend);
})();
